<script setup>
const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  items: {
    type: Array,
    default: () => [],
  },
  inSidebar: {
    type: Boolean,
    default: false,
  },
});

const columnsCount = computed(() => (props.inSidebar ? 3 : 4));

const gridTemplateColumnsCss = computed(
  () => `repeat(${columnsCount.value}, 1fr)`,
);

const itemRows = computed(() => {
  const size = columnsCount.value;
  const rowsArray = [];
  const items = props.items.map((item, itemIndex) => ({
    ...item,
    index: itemIndex,
  }));
  for (let i = 0; i < Math.ceil(items.length / size); i++) {
    rowsArray[i] = items.slice(i * size, i * size + size);
  }

  return rowsArray;
});
</script>

<template>
  <div class="ctr-list-chart" :class="{ container: !inSidebar }">
    <div v-if="title" class="ctr-list-chart__head">
      <span class="ctr-list-chart__font ctr-list-chart__font--head">
        {{ $tp(title) }}
      </span>
    </div>

    <div
      v-for="(row, rowIndex) in itemRows"
      :key="`row-${rowIndex}`"
      class="ctr-list-chart__row"
      :class="{ 'ctr-list-chart__row--reverse': rowIndex % 2 !== 0 }"
    >
      <div
        v-for="(item, itemIndex) in row"
        :key="`item-${itemIndex}`"
        class="ctr-list-chart__item"
        :class="{
          'ctr-list-chart__item--last': item.index === items.length - 1,
          'ctr-list-chart__item--last-in-row':
            rowIndex < itemRows.length - 1 && itemIndex + 1 === row.length,
        }"
      >
        <img
          v-if="item.index + 1 !== items.length"
          class="ctr-list-chart__arrow"
          src="/images/black-arrow-right.svg"
          alt="arrow"
        />

        <p class="ctr-list-chart__font ctr-list-chart__font--num">
          {{ $tp(item.num) }}
        </p>

        <p class="ctr-list-chart__font ctr-list-chart__font--text">
          {{ $tp(item.text) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ctr-list-chart {
  $parent: &;

  display: flex;
  flex-direction: column;
  gap: em(30);

  @include media-breakpoint-down(sm) {
    gap: em(27);
  }

  &__head {
    margin-bottom: em(64);
    padding-top: em(30);
    border-top: 1px solid $color-gray-1;

    @include media-breakpoint-down(sm) {
      margin-bottom: em(36);
    }
  }

  &__row {
    display: grid;
    gap: em(30);

    @include media-breakpoint-up(sm) {
      grid-template-columns: v-bind(gridTemplateColumnsCss);
    }

    &--reverse {
      @include media-breakpoint-up(sm) {
        direction: rtl;
      }

      #{$parent}__arrow {
        @include media-breakpoint-up(sm) {
          left: em(-28);
          right: 0;
          transform: translateY(-50%) rotate(180deg);
        }
      }
    }

    @include media-breakpoint-down(sm) {
      gap: em(27);
    }
  }

  &__item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: em(13);
    padding: em(14) em(15);
    min-height: em(192);
    text-align: center;
    font-size: em(18);
    line-height: em(23, 18);
    background: $color-gray-3;
    border: 1px solid $color-gray-1;
    direction: ltr;

    &--last {
      background: none;
      border-color: $color-red-1;
    }

    &--last-in-row {
      #{$parent}__arrow {
        top: auto;
        right: 0;
        bottom: em(-18);
        left: 50%;
        transform: translateY(0) translateX(-50%) rotate(90deg);
      }
    }

    @include media-breakpoint-down(sm) {
      gap: 0;
      padding: em(38) em(16);
      width: auto;
      min-height: 0;
      text-align: center;
      font-size: em(16);
      line-height: em(21, 16);
    }
  }

  &__arrow {
    position: absolute;
    top: 50%;
    right: em(-28);
    width: em(28);
    transform: translateY(-50%);

    @include media-breakpoint-down(sm) {
      top: auto;
      right: 0;
      bottom: em(-17);
      left: 50%;
      width: em(27);
      transform: translateY(0) translateX(-50%) rotate(90deg);
    }
  }

  &__font {
    &--head {
      font-size: em(56);
      line-height: em(62, 56);

      @include media-breakpoint-down(sm) {
        font-size: em(28);
        line-height: em(31, 28);
      }
    }

    &--num {
      color: $color-red-1;

      @include media-breakpoint-down(sm) {
        display: none;
      }
    }

    &--text {
      margin: auto 0;

      @include media-breakpoint-up(sm) {
        padding-bottom: em(36);
      }
    }
  }
}
</style>
